import {ApolloLink, gql} from "@apollo/client";
import {useEffect, useRef, useState} from "react";
import {useGetCompanyInfoQuery, useTenantChangeVersionQuery} from "../generated/graphql";

export const TENANT_CHANGE_VERSION = gql`
    query TenantChangeVersion {
        tenantChangeVersion
    }
`;

export const LIVE_REFRESH_INTERVAL_MS = 5000;

// Zeitpunkte der fehlerfreien Mutationen dieses Tabs. Jede erhöht normalerweise auch den Tenant-Zähler;
// springt er stärker, kam die Änderung von jemand anderem. Eine eigene Mutation zählt nur zwei Intervalle
// der jeweiligen Ansicht lang: Erhöht der Server nicht (Tenant nicht verifiziert, Redis weg), verfällt sie,
// statt die nächste fremde Änderung zu verdecken.
export const OWN_MUTATION_TTL_MS = 2 * LIVE_REFRESH_INTERVAL_MS;
// Länger als das größte Intervall (30 s) mal zwei; nur damit die Liste nicht wächst.
const OWN_MUTATION_KEEP_MS = 120000;
let ownMutationTimes: Array<number> = [];

export const countOwnMutation = (): void => {
    const now = Date.now();
    ownMutationTimes = ownMutationTimes.filter((time) => time > now - OWN_MUTATION_KEEP_MS);
    ownMutationTimes.push(now);
};

const ownMutationsSince = (since: number, ttl: number): number => {
    const notBefore = Math.max(since, Date.now() - ttl);

    return ownMutationTimes.filter((time) => time > notBefore).length;
};

export const ownMutationLink = new ApolloLink((operation, forward) => {
    const isMutation = operation.query.definitions.some((definition) => definition.kind === "OperationDefinition" && definition.operation === "mutation");

    return isMutation
        ? forward(operation).map((response) => {
              if (!response.errors?.length) countOwnMutation();

              return response;
          })
        : forward(operation);
});

const useDocumentVisible = (): boolean => {
    const [visible, setVisible] = useState(typeof document === "undefined" || document.visibilityState !== "hidden");

    useEffect(() => {
        const onChange = () => setVisible(document.visibilityState !== "hidden");
        document.addEventListener("visibilitychange", onChange);

        return () => document.removeEventListener("visibilitychange", onChange);
    }, []);

    return visible;
};

/**
 * Live-Aktualisierung (Beta, Tenant-Flag `featureLiveRefresh`): ruft `refetch`, sobald eine andere
 * Person im Tenant etwas geändert hat. Pollt dafür den Änderungszähler des Tenants alle `intervalMs`
 * (Standard 5 s), nur bei sichtbarem Tab; mehrere Änderungen innerhalb eines Intervalls lösen ein
 * einziges Nachladen aus. Teure Ansichten wählen ein längeres Intervall (Messung WM 14.10.). Der erste Stand nach dem Einschalten löst kein Nachladen aus.
 *
 * Nur für Listen gedacht: Formulare mit Eingaben nie damit versehen.
 * `refetch` läuft nur, wenn der Zähler stärker gesprungen ist als die eigenen Mutationen dieses Tabs
 * seit dem letzten Stand: Nach eigenen Änderungen lädt die Ansicht selbst nach (`onDone`, `refetch`).
 * Messung WM 19.10.: beim Abgleichen kam etwa alle 20 s eine eigene Mutation, die Liste lud doppelt.
 * ponytail: eigene und fremde Änderung im selben Intervall gelten zusammen als fremd; ebenso,
 * wenn der Poll den Zähler sieht, bevor die Antwort der eigenen Mutation da ist (Millisekunden).
 */
export const useLiveRefresh = (refetch: () => Promise<unknown>, paused = false, intervalMs = LIVE_REFRESH_INTERVAL_MS): void => {
    const {data: companyInfoData} = useGetCompanyInfoQuery({fetchPolicy: "cache-first"});
    const enabled = Boolean(companyInfoData?.companyInfo?.subscription?.featureLiveRefresh);
    const visible = useDocumentVisible();
    const {data} = useTenantChangeVersionQuery({
        skip: !enabled || !visible || paused,
        pollInterval: intervalMs,
        fetchPolicy: "network-only",
    });
    const version = data?.tenantChangeVersion;
    const lastSeen = useRef<number | undefined>();
    const lastCheck = useRef(Date.now());
    const refetchRef = useRef(refetch);
    refetchRef.current = refetch;

    useEffect(() => {
        if (version === undefined) return;
        if (lastSeen.current !== undefined && version - lastSeen.current > ownMutationsSince(lastCheck.current, 2 * intervalMs)) {
            refetchRef.current().catch(() => undefined);
        }
        // Auch beim ersten Stand: eigene Mutationen davor stecken schon im Zähler.
        lastCheck.current = Date.now();
        lastSeen.current = version;
    }, [version]);
};
